import { useEffect, useRef } from "react";
import {
  X,
  FileText,
  CalendarClock,
  Quote,
  Info,
  Tag,
  Clock,
  Sparkles,
  Image,
  MessageSquare,
  Play,
} from "lucide-react";

const gradientPrimary =
  "linear-gradient(rgb(85, 37, 235) 0%, rgb(26, 14, 65) 100%)";

export const MessageViewModal = ({ isOpen, onClose, data }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !data) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-black/60 via-black/40 to-black/60 backdrop-blur-md flex justify-center items-center px-4 py-4 overflow-y-auto">
      <div
        ref={modalRef}
        className="bg-white rounded-2xl animate-scale-in shadow-2xl border border-gray-100 w-full max-w-3xl max-h-[90vh] purple-scroll overflow-auto relative flex flex-col"
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -right-32 w-64 h-64 bg-gradient-to-br from-blue-400/8 to-purple-400/8 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-32 -left-32 w-64 h-64 bg-gradient-to-tr from-indigo-400/8 to-pink-400/8 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative ">
          <div
            className="p-4 rounded-[10px]"
            style={{ background: gradientPrimary }}
          >
            <div className="flex justify-between items-start">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl">
                  {data.type === "media" ? (
                    <Image className="w-5 h-5 text-white" />
                  ) : (
                    <FileText className="w-5 h-5 text-white" />
                  )}
                </div>
                <div>
                  <h1 className="text-lg font-bold text-white mb-0.5 tracking-tight">
                    {data.type === "media"
                      ? "Media Template"
                      : "Message Template"}
                  </h1>
                  <p className="text-white/80 text-sm font-medium flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Template Overview & Details
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg text-white transition-all duration-200 hover:scale-110"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="relative px-4 py-4 space-y-4">
          {/* Template Information Section */}
          <section className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg">
                <Info className="w-4 h-4 text-indigo-600" />
              </div>
              <h2 className="text-base font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                Template Information
              </h2>
            </div>

            {/* Grid - Enhanced with type field */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {[
                {
                  label: "Meta Template ID",
                  value: data.metaTemplateId,
                  gradient: "from-emerald-500 to-teal-500",
                  bgGradient: "from-emerald-50 to-teal-50",
                },
                {
                  label: "Template Name",
                  value: data.templateName,
                  gradient: "from-violet-500 to-purple-500",
                  bgGradient: "from-violet-50 to-purple-50",
                },
                {
                  label: "Template Type",
                  value: data.type.charAt(0).toUpperCase() + data.type.slice(1),
                  gradient: "from-blue-500 to-indigo-500",
                  bgGradient: "from-blue-50 to-indigo-50",
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className={`group relative p-3 bg-gradient-to-br ${item.bgGradient} border border-white/50 rounded-xl shadow-sm hover:shadow-md transition-all duration-300`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                        {item.label}
                      </p>
                      <p className="text-sm font-bold text-gray-800 break-words">
                        {item.value}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Content Section - Dynamic based on type */}
          {data.type === "text" && data.sampleMessage && (
            <section className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-gradient-to-br from-orange-100 to-red-100 rounded-lg">
                  <MessageSquare className="w-4 h-4 text-orange-600" />
                </div>
                <h2 className="text-base font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  Sample Message Preview
                </h2>
              </div>

              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl blur opacity-10 group-hover:opacity-20 transition-opacity duration-300"></div>
                <blockquote className="relative bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 rounded-xl border-l-4 border-indigo-500 shadow-sm">
                  <div className="absolute top-3 right-3 text-indigo-300">
                    <Quote className="w-5 h-5" />
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed font-medium italic pr-8">
                    "{data.sampleMessage}"
                  </p>
                </blockquote>
              </div>
            </section>
          )}

          {data.type === "media" && data.mediaUrl && (
            <section className="space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-gradient-to-br from-purple-100 to-pink-100 rounded-lg">
                  <Image className="w-4 h-4 text-purple-600" />
                </div>
                <h2 className="text-base font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  Media Content Preview
                </h2>
              </div>

              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl blur opacity-10 group-hover:opacity-20 transition-opacity duration-300"></div>
                <div className="relative bg-gradient-to-br from-purple-50 via-white to-pink-50 p-4 rounded-xl border-l-4 border-purple-500 shadow-sm">
                  <div className="flex items-center justify-center">
                    <div className="relative max-w-md w-full">
                      <img
                        src={data.mediaUrl}
                        alt="Media Template Preview"
                        className="w-[200px] mx-auto"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                      <div className="hidden items-center justify-center h-48 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg border-2 border-dashed border-gray-300">
                        <div className="text-center">
                          <Image className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">
                            Media preview unavailable
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          <section className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <div className="p-1.5 bg-gradient-to-br from-pink-100 to-rose-100 rounded-lg">
                <Tag className="w-4 h-4 text-pink-600" />
              </div>
              <h2 className="text-lg font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                Dynamic Placeholders
              </h2>
            </div>

            {data.placeholders.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {data.placeholders
                  .sort((a, b) => a.position - b.position)
                  .map((placeholder, i) => (
                    <span
                      key={placeholder._id}
                      className="group relative px-3 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-full shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105 cursor-default"
                    >
                      <span className="relative z-10 text-xs font-mono flex items-center gap-1">
                        <span className="bg-white/20 px-1.5 py-0.5 rounded text-xs">
                          {placeholder.position}
                        </span>
                        {placeholder.label}
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </span>
                  ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg text-gray-500 text-sm">
                  <Tag className="w-4 h-4" />
                  No placeholders defined for this template
                </div>
              </div>
            )}
          </section>

          {/* Timestamps Section */}
          <section className="pt-3 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                {
                  label: "Created",
                  value: new Date(data.createdAt).toLocaleString(),
                  icon: <CalendarClock className="w-4 h-4" />,
                  gradient: "from-green to-emerald-500",
                },
                {
                  label: "Last Updated",
                  value: new Date(data.updatedAt).toLocaleString(),
                  icon: <Clock className="w-4 h-4" />,
                  gradient: "from-blue-500 to-cyan-500",
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200 shadow-sm"
                >
                  <div
                    className={`p-2 bg-gradient-to-br ${item.gradient} rounded-lg text-white shadow-sm`}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {item.label}
                    </p>
                    <p className="text-sm font-bold text-gray-800 mt-0.5">
                      {item.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MessageViewModal;
